import { useEffect, useMemo, useState } from 'react';
import type { ZxcvbnFactory } from '@zxcvbn-ts/core';
import {
  getPasswordStrengthUserInputs,
  validatePassword,
  PasswordValidationResult,
} from '@dill/dill-shared';

/**
 * The zxcvbn dictionaries are ~400KB, so they are imported dynamically and only when a password
 * is actually being chosen — they must never land in the main bundle, which every page pays for.
 */
let checkerPromise: Promise<ZxcvbnFactory> | null = null;

export const loadPasswordStrengthChecker = () => {
  if (!checkerPromise) {
    checkerPromise = Promise.all([
      import('@zxcvbn-ts/core'),
      import('@zxcvbn-ts/language-common'),
      import('@zxcvbn-ts/language-en'),
    ]).then(
      ([core, common, en]) =>
        new core.ZxcvbnFactory({
          dictionary: { ...common.dictionary, ...en.dictionary },
          graphs: common.adjacencyGraphs,
          translations: en.translations,
          useLevenshteinDistance: true,
        })
    );
  }
  return checkerPromise;
};

export const usePasswordValidation = ({
  password,
  email,
  personalTerms,
  isEnabled = true,
}: {
  password: string;
  email?: string | null;
  personalTerms?: (string | null | undefined)[];
  /**
   * Off outside the screens where a password is being chosen. Someone signing in with an existing
   * password must not pay for the dictionary download, so this gates it rather than the caller
   * simply ignoring the result.
   */
  isEnabled?: boolean;
}): PasswordValidationResult => {
  const [strengthScore, setStrengthScore] = useState<number | null>(null);
  const [isStrengthCheckSkipped, setIsStrengthCheckSkipped] = useState(false);

  const userInputs = useMemo(
    () => getPasswordStrengthUserInputs({ email, personalTerms }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, (personalTerms ?? []).join('|')]
  );

  // Start the download before the first keystroke so the checklist is not stuck pending while
  // the user waits to submit.
  useEffect(() => {
    if (isEnabled) {
      loadPasswordStrengthChecker().catch(() => setIsStrengthCheckSkipped(true));
    }
  }, [isEnabled]);

  useEffect(() => {
    if (!password || !isEnabled) {
      setStrengthScore(null);
      return;
    }
    let isCurrent = true;
    setStrengthScore(null);
    loadPasswordStrengthChecker()
      .then((checker) => {
        if (isCurrent) {
          setStrengthScore(checker.check(password, userInputs).score);
        }
      })
      .catch(() => {
        // Nothing to fall back on in the browser: block nobody, the backend re-checks on setup.
        if (isCurrent) {
          setIsStrengthCheckSkipped(true);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [password, userInputs, isEnabled]);

  return useMemo(
    () =>
      validatePassword({
        password,
        email,
        personalTerms,
        strengthScore,
        isStrengthCheckSkipped,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [password, email, (personalTerms ?? []).join('|'), strengthScore, isStrengthCheckSkipped]
  );
};
